import { useApolloClient } from "@apollo/client";
import React, {
    createRef, ReactElement, useEffect, useState, useMemo,
} from "react";
import { ListGroup, Modal, Form } from "react-bootstrap";
import { RiHashtag } from "react-icons/ri";
import useKeyboardShortcuts, { useKeyboardState } from "../../../contexts/KeyboardShortcutContext";
import {
    Article,
    useSearchArticlesQuery,
    GetArticleDocument,
    TicketMessageArticleInput,
} from "../../../__generated__/api";
import { HtmlRenderer } from "../../html-render/HtmlRenderer";
import { LoadingIcon } from "../../loading/Loading";
import Popover from "../../popover/Popover";

const KEYBOARD_CONTEXT = "chat";
const SEARCH_KEYBOARD_CONTEXT = "article-search";

export default function AnswerFinder({
    onAnswerSelected,
    onCannedMessageSelected,
}: {
    onAnswerSelected: (article: TicketMessageArticleInput) => void,
    onCannedMessageSelected: (body: string | undefined) => void,
}): ReactElement {
    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const keyboardState = useKeyboardState();

    const [search, setSearch] = useState("");
    const searchRef = useMemo(() => createRef<HTMLInputElement>(), []);

    const [isCannedMessage, setIsCannedMessage] = useState<boolean>(false);

    const client = useApolloClient();

    const { data: searchData, loading: searchLoading } = useSearchArticlesQuery({
        variables: {
            query: search,
            options: {
                shouldSearchExternal: true,
            },
        },
        skip: search == null || search.length < 2,
    });

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "cmd+3",
            context: KEYBOARD_CONTEXT,
            handler: () => {
                setShouldShow(true);
            },
        });
    });

    const searchResults = searchData?.articles.results ?? [];

    const selected = (selectedIndex >= 0
        ? (selectedIndex % searchResults.length)
        : Math.abs(searchResults.length + ((selectedIndex + 1) % searchResults.length)) - 1);

    const getArticleText = async (id: string) => {
        const results = await client.query({
            query: GetArticleDocument,
            variables: {
                id,
            },
        });
        return results.data.article.body.html;
    };

    const handleSelected = (item: Article) => {
        if (!isCannedMessage) {
            onAnswerSelected({
                id: item.id,
                title: item.title,
                portalId: item.portalId,
                previewText: item.previewText ?? "",
            });
        } else {
            (async () => {
                const results = await getArticleText(item.id);
                onCannedMessageSelected(results);
            })();
        }
        setShouldShow(false);
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "esc",
            context: SEARCH_KEYBOARD_CONTEXT,
            handler: evt => {
                evt?.stopPropagation();
                setShouldShow(false);
            },
        });

        shortcuts.on({
            shortcut: "down",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr + 1);
            },
        });
        shortcuts.on({
            shortcut: "up",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr - 1);
            },
        });
        shortcuts.on({
            shortcut: "enter",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: () => {
                const selectedResult = searchResults[selected];
                handleSelected(selectedResult as Article);
            },
        });
    });

    useEffect(() => {
        if (shouldShow && searchRef.current) {
            searchRef.current.selectionStart = 0;
            searchRef.current.selectionEnd = searchRef.current.value.length;
            searchRef.current.focus();
        }
    }, [shouldShow, searchRef]);

    useEffect(() => {
        if (shouldShow) {
            keyboardState.pushContext(SEARCH_KEYBOARD_CONTEXT);
        } else {
            keyboardState.popContext(SEARCH_KEYBOARD_CONTEXT);
        }
    }, [shouldShow]);

    return (
        <>
            <Modal
                show={shouldShow}
                contentClassName="rounded shadow border-0"
                centered
            >
                <Modal.Body className="overflow-auto flex-grow-1 flex-shrink-1 bg-blue-dark text-white rounded">
                    <div>
                        <small className="text-blue-light fw-lighter">
                            Article Search
                        </small>

                        <input
                            ref={searchRef}
                            type="search"
                            placeholder="Search..."
                            className="w-100 rounded no-border text-white mt-1"
                            value={search}
                            onChange={evt => setSearch(evt.target.value)}
                        />

                        <div className="text-light mt-3 d-flex align-items-center">
                            <Form.Check
                                type="switch"
                                className="fs-6 text-blue-light"
                                id="edit-before-sending"
                                checked={isCannedMessage}
                                onChange={() => setIsCannedMessage(isCanned => !isCanned)}
                                label="Edit before sending"
                            />
                            <Popover buttonClassName="text-blue-light ms-1" title="Canned Response">
                                <div>
                                    Inserts the article body into the conversation field.
                                    Edit before sending to customize the message as needed.
                                </div>
                            </Popover>

                        </div>

                        {searchLoading && (
                            <div className="mt-2 text-center"><LoadingIcon /></div>
                        )}

                        {search && searchResults.isNotEmpty && (
                            <ListGroup variant="flush" className="mt-4">
                                {searchResults?.map((item, index) => {
                                    const classes = ["border-bottom-0 my-1 cursor-pointer"];

                                    if (index === selected) {
                                        classes.push("bg-light text-black");
                                    } else {
                                        classes.push("bg-blue-dark text-light");
                                    }
                                    return (
                                        <ListGroup.Item
                                            key={item.id}
                                            className={classes.join(" ")}
                                            onClick={() => handleSelected(item as Article)}
                                        >

                                            <strong>
                                                {item.title}
                                            </strong>
                                            <HtmlRenderer html={item.previewText ?? ""} />

                                        </ListGroup.Item>

                                    );
                                })}
                            </ListGroup>
                        )}
                        {!searchLoading && search && searchResults?.isEmpty && (
                            <div className="w-100 text-center mt-3">
                                <small className="text-light ">Sorry no articles that match.</small>
                            </div>
                        )}
                    </div>
                </Modal.Body>

            </Modal>

            <button type="button" className="btn btn-none p-1 text-gray-500 pe-1 " onClick={() => setShouldShow(true)}>
                <RiHashtag />
            </button>
        </>

    );
}
